import { useEffect, useRef, useState } from "react"
import Icon from "../../components/Icon"
import { formatDateWithTime, formatMoney } from "../../utils/format"
import { getIncomeSourceMeta, getProviderSourceMeta } from "./utbillsHelpers"
import api from "../../utils/api"
import { getApiErrorMessage } from "../../utils/errorMessage"
import AddCategoryModal from "./AddCategoryModal"
import AddBillModal from "./AddBillModal"
import WaiveBillModal from "./WaiveBillModal"
import AutoBillModal from "./AutoBillModal"
import PropertyAutoInvoicing from "./PropertyAutoInvoicing"
import ProviderMoneySummary from "./ProviderMoneySummary"
import ProviderCustomersPanel from "./ProviderCustomersPanel"
import CustomerStatementModal from "./CustomerStatementModal"
import ItemCategoryDropdown from "./ItemCategoryDropdown"
import CustomerBillFilter, { getInvoiceCustomerKey } from "./CustomerBillFilter"
import EditInvoiceModal from "./EditInvoiceModal"
import StatementTable from "./StatementTable"
import UtilityInvoiceStatusBadge from "./UtilityInvoiceStatusBadge"
import {
  getProviderUtilityCashMovement,
  getUtilityInvoiceSettlement,
  getUtilityPaymentDescription,
  getUtilitySettlementId,
  getVisibleUtilitySettlementRows,
  normalizeUtilityAllocations,
} from "./utilitySettlement"

// Bridge bill numbers are internal "TYPE-<mongo id>" codes with no meaning to a
// user (e.g. "WATER-690117ff90e1203af51d4beb"). Hide those; keep human-entered
// numbers like "TRASH-39874887".
const BRIDGE_BILL_NUMBER_RE = /^(WATER|TRASH|UTIL)-[a-f0-9]{24}$/i
const isBridgeBillNumber = (billNumber) => BRIDGE_BILL_NUMBER_RE.test(String(billNumber || "").trim())

// Tidy a stored invoice description for display: drop the leading "… invoice
// for" (the row is already shown as a bill) and round the noisy 4-decimal
// "excess units" figure down to something a person can read.
function formatBillDescription(description) {
  const text = String(description || "").trim().replace(/^.*?\binvoice\s+for\s+/i, "")
  return text.replace(/\(\s*(\d+(?:\.\d+)?)\s*excess\s+units?\s*\)/i, (match, value) => {
    const amount = Number(value)
    if (!Number.isFinite(amount)) return match
    const label = Number.isInteger(amount) ? String(amount) : amount.toFixed(1)
    return `(${label} ${amount === 1 ? "unit" : "units"} over)`
  })
}

function SourceBadge({ source }) {
  const meta = getProviderSourceMeta(source)
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${meta.className}`}>
      <Icon name={meta.icon} size="0.6rem" />
      {meta.label}
    </span>
  )
}

function IncomeSourceBadge({ source }) {
  const meta = getIncomeSourceMeta(source)
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${meta.className}`}>
      <Icon name={meta.icon} size="0.6rem" />
      {meta.label}
    </span>
  )
}

function BillingConsole({
  providers,
  selectedProviderId,
  setSelectedProviderId,
  selectedProvider,
  accounts,
  categories = [],
  providerInvoices,
  providerStatement,
  providerCustomers = [],
  providerCustomersLoading = false,
  onLoadCustomerStatement,
  onCreateCategory,
  onConfigureAutoInvoice,
  onCreateInvoice,
  onCreateBill,
  onWaiveInvoice,
  onEditInvoice,
  onDeleteInvoice,
  onSearchCustomers,
  deepLinkProviderId,
  deepLinkModal,
  deepLinkPropertyId,
  deepLinkUnitId,
}) {
  const [modal, setModal] = useState(null)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [statementCustomer, setStatementCustomer] = useState(null)
  const [billCustomerKey, setBillCustomerKey] = useState("")
  const appliedDeepLinkRef = useRef("")
  // Landing view: show a card per company the user runs. Picking a card opens
  // the full console (summary, categories, bills, income) for that company.
  // Deep-links from the dashboard jump straight into the picked company.
  const [showGrid, setShowGrid] = useState(!deepLinkProviderId)

  const openCompany = (providerId) => {
    setSelectedProviderId(providerId)
    setShowGrid(false)
  }

  useEffect(() => {
    setBillCustomerKey("")
    setStatementCustomer(null)
  }, [selectedProviderId])

  const isManagementCompany = selectedProvider?.source === "management_company"

  // FRP companies: auto-invoicing is per property. Load the property list for the
  // picker; native providers keep company-level categories + bill accounts.
  const [properties, setProperties] = useState([])
  const [propertiesLoading, setPropertiesLoading] = useState(false)
  const [propertiesError, setPropertiesError] = useState("")
  const [selectedPropertyId, setSelectedPropertyId] = useState("")

  useEffect(() => {
    if (!selectedProviderId || !isManagementCompany) {
      setProperties([])
      setSelectedPropertyId("")
      setPropertiesError("")
      return
    }
    let active = true
    setPropertiesLoading(true)
    setPropertiesError("")
    api
      .get(`/ut-bills/providers/${encodeURIComponent(selectedProviderId)}/properties`)
      .then((res) => {
        if (!active) return
        const list = Array.isArray(res?.data?.properties) ? res.data.properties : []
        setProperties(list)
        setSelectedPropertyId((current) => {
          if (current && list.some((p) => String(p._id) === String(current))) return current
          if (deepLinkPropertyId && list.some((p) => String(p._id) === String(deepLinkPropertyId))) {
            return deepLinkPropertyId
          }
          return list[0]?._id || ""
        })
      })
      .catch((err) => {
        if (active) setPropertiesError(getApiErrorMessage(err, "Failed to load properties."))
      })
      .finally(() => {
        if (active) setPropertiesLoading(false)
      })
    return () => {
      active = false
    }
  }, [selectedProviderId, isManagementCompany, deepLinkPropertyId])

  useEffect(() => {
    if (!deepLinkModal || !selectedProvider) return
    if (deepLinkProviderId && String(selectedProvider._id) !== String(deepLinkProviderId)) return

    const key = [
      deepLinkProviderId || selectedProvider._id,
      deepLinkModal,
      deepLinkPropertyId || "",
      deepLinkUnitId || "",
    ].join("|")
    if (appliedDeepLinkRef.current === key) return

    if (deepLinkModal === "bill") {
      setShowGrid(false)
      setModal("bill")
      appliedDeepLinkRef.current = key
    }
  }, [deepLinkModal, deepLinkProviderId, deepLinkPropertyId, deepLinkUnitId, selectedProvider])

  const selectedPropertyObj = properties.find((p) => String(p._id) === String(selectedPropertyId)) || null

  // Native providers configure auto-invoicing at company level (property: null).
  const companyCategories = categories.filter((c) => !c.property)
  const autoBillingActive = companyCategories.some((c) => c.autoInvoiceEnabled)

  const allPayments = Array.isArray(providerStatement?.settlements)
    ? providerStatement.settlements
    : Array.isArray(providerStatement?.payments)
      ? providerStatement.payments
      : []
  const invoices = Array.isArray(providerInvoices) ? providerInvoices : []
  const outstandingInvoices = invoices.filter(
    (invoice) => getUtilityInvoiceSettlement(invoice).outstandingAmount > 0,
  )
  const hasProviders = providers.length > 0

  // The bill list only narrows once a customer is picked, so typing in the
  // search box never reshuffles a long table under the user's fingers.
  const visibleInvoices = billCustomerKey
    ? invoices.filter((invoice) => getInvoiceCustomerKey(invoice) === billCustomerKey)
    : invoices

  const filteredPayments = allPayments.filter((payment) => {
    if (!selectedProvider) return true
    const paymentProviderId = String(payment?.provider?._id || payment?.provider || "")
    const paymentSourceCompanyId = String(payment?.provider?.sourceCompany || "")
    const allocations = normalizeUtilityAllocations(payment)
    if (selectedProvider.source === "management_company") {
      return paymentSourceCompanyId === String(selectedProvider.sourceId || "") || allocations.some(
        (allocation) => String(allocation?.provider?.sourceCompany || "") === String(selectedProvider.sourceId || ""),
      )
    }
    return paymentProviderId === String(selectedProvider._id || "") || allocations.some(
      (allocation) => String(allocation?.provider?._id || allocation?.provider || "") === String(selectedProvider._id || ""),
    )
  })
  // The provider API already returns one scoped projection per provider. Keep
  // two projections of the same parent payment when it paid two companies;
  // the legacy fallback still needs client-side mirror de-duplication.
  const payments = Array.isArray(providerStatement?.settlements)
    ? filteredPayments
    : getVisibleUtilitySettlementRows(filteredPayments)

  const totalIncome = payments.reduce(
    (sum, payment) => sum + getProviderUtilityCashMovement(payment),
    0,
  )

  // Item/Category list: company-level for native providers; for FRP companies
  // these are the manual-billing ones (property-scoped live in the panel).
  const chipCategories = isManagementCompany ? companyCategories : categories
  const countAccountsInCategory = (category) =>
    accounts.filter((account) => String(account.category) === String(category._id)).length

  // Due / Received / Paid out / Net per company, computed once on the server so
  // the cards and the open company header always agree.
  const summaryByProviderId = new Map(
    (Array.isArray(providerStatement?.providerSummaries) ? providerStatement.providerSummaries : [])
      .map((row) => [String(row.providerId), row]),
  )
  const getProviderSummary = (providerId) => summaryByProviderId.get(String(providerId)) || null

  return (
    <div className="space-y-5">
      <div className="h-px w-full bg-slate-200" />

      {!hasProviders ? (
        <section className="rounded-2xl border border-dashed border-teal-300 bg-gradient-to-br from-teal-50 to-emerald-50 p-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-600 text-white shadow-lg">
            <Icon name="shop" size="1.6rem" />
          </div>
          <h3 className="mt-4 text-base font-black text-slate-900">Start billing your customers</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-600">
            Register a billing company to send water, trash, rent or any other bills. Customers pay you straight from their UT Bills wallet.
          </p>
          <p className="mx-auto mt-3 max-w-sm rounded-xl border border-teal-200 bg-white/70 px-4 py-2.5 text-xs font-semibold text-teal-800">
            Open the <span className="font-black">Register</span> menu on your dashboard and choose <span className="font-black">UT-Bills Company</span> to create one or use an existing FRP company.
          </p>
        </section>
      ) : showGrid ? (
        /* Landing: one card per company the user runs */
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {providers.map((provider) => (
            /* The whole card opens the company, so it carries a visible
               "Open" cue at all times: a hover-only hint never appears on a
               touch screen, which is the main platform here. */
            <button
              key={provider._id}
              type="button"
              onClick={() => openCompany(provider._id)}
              aria-label={`Open ${provider.name}`}
              className="group flex cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-lg active:translate-y-0 active:border-teal-400 active:bg-teal-50/40 active:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600 ring-1 ring-teal-100 transition-colors group-hover:bg-teal-600 group-hover:text-white">
                  <Icon name="shop" size="1.3rem" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-base font-black leading-tight text-slate-900">{provider.name}</h3>
                  <p className="mt-1 truncate text-xs text-slate-500">
                    {provider.telephoneNumber || "No telephone"}
                    {provider.location ? ` · ${provider.location}` : ""}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <SourceBadge source={provider.source} />
                    {provider.locked && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                        <Icon name="lock-fill" size="0.6rem" /> Locked
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <ProviderMoneySummary summary={getProviderSummary(provider._id)} className="mt-3" />

              <div className="mt-3 flex items-center justify-end border-t border-slate-100 pt-2.5">
                <span className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 transition-colors group-hover:text-teal-800">
                  Open
                  <Icon
                    name="chevron-right"
                    size="0.7rem"
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <>
          {/* Back to company cards + quick switcher chips */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowGrid(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50"
            >
              <Icon name="grid" size="0.8rem" />
              All companies
            </button>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {providers.map((provider) => {
                const active = String(provider._id) === String(selectedProviderId)
                return (
                  <button
                    key={provider._id}
                    type="button"
                    onClick={() => setSelectedProviderId(provider._id)}
                    className={`flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${
                      active
                        ? "border-teal-500 bg-teal-50 text-teal-800 ring-2 ring-teal-100"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <span className="max-w-[10rem] truncate">{provider.name}</span>
                    <SourceBadge source={provider.source} />
                  </button>
                )
              })}
            </div>
          </div>

          {selectedProvider && (
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              {/* Provider name + Issue bill */}
              <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-base font-black text-slate-900">{selectedProvider.name}</h3>
                    <SourceBadge source={selectedProvider.source} />
                    {selectedProvider.locked && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                        <Icon name="lock-fill" size="0.6rem" /> Locked
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {selectedProvider.telephoneNumber || "No telephone"}
                    {selectedProvider.location ? ` · ${selectedProvider.location}` : ""}
                  </p>
                  <ProviderMoneySummary
                    summary={getProviderSummary(selectedProvider._id)}
                    className="mt-2 max-w-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setModal("bill")}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-teal-600 px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-teal-700 sm:flex-none"
                  >
                    <Icon name="receipt-cutoff" size="0.8rem" />
                    Issue bill
                  </button>
                </div>
              </div>

              {/* Item/Category (manual billing) */}
              <div className="border-b border-slate-100 px-4 py-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Item/Category ({chipCategories.length})
                  </p>
                  <button
                    type="button"
                    onClick={() => setModal("category")}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-bold text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <Icon name="plus-lg" size="0.6rem" />
                    Add
                  </button>
                </div>

                {chipCategories.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    No item or category yet. Add one to group who you bill.
                  </p>
                ) : (
                  <ItemCategoryDropdown categories={chipCategories} countFor={countAccountsInCategory} />
                )}

                {/* Native providers: company-level auto-invoicing. FRP companies do
                    this per property in the section below. */}
                {!isManagementCompany && (
                  <button
                    type="button"
                    onClick={() => setModal("autobill")}
                    className={`mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-bold transition-colors ${
                      autoBillingActive
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        : "border-teal-400 bg-teal-600 text-white hover:bg-teal-700"
                    }`}
                  >
                    <Icon name="arrow-repeat" size="0.8rem" />
                    {autoBillingActive ? "Manage auto-billing" : "Set up auto invoicing"}
                  </button>
                )}
              </div>

              {/* FRP companies: per-property auto-invoicing */}
              {isManagementCompany ? (
                <div className="p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Auto-invoicing by property</p>
                  </div>

                  {propertiesError && (
                    <p className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                      {propertiesError}
                    </p>
                  )}

                  {propertiesLoading ? (
                    <div className="h-10 animate-pulse rounded-xl bg-slate-100" />
                  ) : properties.length === 0 ? (
                    <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
                      This company has no properties yet. Add a property in the Rentals module first.
                    </p>
                  ) : (
                    <>
                      {/* Property picker */}
                      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
                        {properties.map((property) => {
                          const active = String(property._id) === String(selectedPropertyId)
                          return (
                            <button
                              key={property._id}
                              type="button"
                              onClick={() => setSelectedPropertyId(property._id)}
                              className={`flex shrink-0 items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${
                                active
                                  ? "border-teal-500 bg-teal-50 text-teal-800 ring-2 ring-teal-100"
                                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              <Icon name="building" size="0.7rem" />
                              <span className="max-w-[10rem] truncate">{property.name}</span>
                            </button>
                          )
                        })}
                      </div>

                      {selectedPropertyObj && (
                        <PropertyAutoInvoicing
                          key={selectedPropertyObj._id}
                          providerRef={selectedProviderId}
                          property={selectedPropertyObj}
                        />
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="p-4">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                    Bill accounts ({accounts.length})
                  </p>
                  {accounts.length === 0 ? (
                    <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
                      No bill accounts yet. Add one to start billing a customer.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {accounts.map((account) => (
                        <div key={account._id} className="rounded-xl border border-slate-200 bg-white p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-bold text-slate-800">{account.customer?.name || "Customer"}</span>
                            <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-600">
                              {account.billNumber}
                            </span>
                          </div>
                          <p className="mt-1 truncate text-xs text-slate-500">
                            {account.customer?.phone || ""}
                            {account.label ? ` · ${account.label}` : ""}
                          </p>
                          {account.categoryName && (
                            <span className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[10px] font-bold text-teal-700">
                              <Icon name="tag" size="0.55rem" />
                              {account.categoryName}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Customer balances: who owes this company, biggest debt first */}
          {selectedProvider && (
            <ProviderCustomersPanel
              customers={providerCustomers}
              loading={providerCustomersLoading}
              onOpenStatement={setStatementCustomer}
            />
          )}

          {/* Issued bills */}
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-black text-slate-900">Issued bills</h3>
                <span
                  className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${
                    outstandingInvoices.length > 0
                      ? "bg-rose-50 text-rose-700 ring-rose-100"
                      : "bg-emerald-50 text-emerald-700 ring-emerald-100"
                  }`}
                >
                  <Icon
                    name={outstandingInvoices.length > 0 ? "exclamation-circle-fill" : "check-circle-fill"}
                    size="0.6rem"
                  />
                  {outstandingInvoices.length} outstanding
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Waivers reduce customer debt without moving wallet funds.
              </p>
            </div>
            <div className="border-b border-slate-100 px-4 py-3">
              <CustomerBillFilter
                invoices={invoices}
                selectedKey={billCustomerKey}
                onSelect={setBillCustomerKey}
              />
            </div>
            <StatementTable
              rows={visibleInvoices}
              rowKey={(invoice) => invoice._id}
              emptyText={
                billCustomerKey
                  ? "This customer has no bills from this company."
                  : "No bills issued from this company yet."
              }
              columns={[
                {
                  label: "Date",
                  width: "18%",
                  render: (invoice) => formatDateWithTime(invoice.date),
                },
                {
                  label: "Customer",
                  align: "left",
                  render: (invoice) => {
                    return (
                      <div>
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="font-semibold text-slate-800">
                            {invoice.customerName || invoice.customerIdNumber || "Customer"}
                          </span>
                          {invoice.billNumber && !isBridgeBillNumber(invoice.billNumber) && (
                            <span className="rounded bg-slate-100 px-1 font-mono text-[9px] font-semibold text-slate-500">
                              {invoice.billNumber}
                            </span>
                          )}
                        </div>
                        <div className="text-slate-500">{formatBillDescription(invoice.description)}</div>
                        <div className="mt-0.5">
                          <UtilityInvoiceStatusBadge invoice={invoice} compact />
                        </div>
                      </div>
                    )
                  },
                },
                {
                  label: "Total",
                  width: "15%",
                  align: "right",
                  render: (invoice) => (
                    <span className="font-semibold text-slate-700">{formatMoney(invoice.amount || 0)}</span>
                  ),
                },
                {
                  label: "Due",
                  width: "15%",
                  align: "right",
                  render: (invoice) => {
                    const due = getUtilityInvoiceSettlement(invoice).outstandingAmount
                    return (
                      <span className={`font-bold ${due > 0 ? "text-red-600" : "text-emerald-700"}`}>
                        {formatMoney(due)}
                      </span>
                    )
                  },
                },
                {
                  label: "",
                  width: "24%",
                  align: "center",
                  render: (invoice) => {
                    const invoiceSettlement = getUtilityInvoiceSettlement(invoice)
                    const canWaive = invoiceSettlement.outstandingAmount > 0
                    const canMutate = invoiceSettlement.totalSettledAmount <= 0
                    return (
                      <div className="flex flex-col items-stretch gap-1">
                        <button
                          type="button"
                          disabled={!canMutate}
                          onClick={() => {
                            setSelectedInvoice(invoice)
                            setModal("edit")
                          }}
                          className="w-full whitespace-nowrap rounded-lg border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-bold text-sky-700 transition-colors hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={!canMutate}
                          onClick={async () => {
                            if (!window.confirm("Delete this utility invoice? This cannot be undone.")) return
                            try {
                              await onDeleteInvoice(selectedProvider?._id, invoice._id)
                            } catch (error) {
                              window.alert(error?.response?.data?.error || error.message || "Failed to delete invoice")
                            }
                          }}
                          className="w-full whitespace-nowrap rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] font-bold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          disabled={!canWaive}
                          onClick={() => {
                            setSelectedInvoice(invoice)
                            setModal("waive")
                          }}
                          className="w-full whitespace-nowrap rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Waive
                        </button>
                      </div>
                    )
                  },
                },
              ]}
            />
          </section>

          {/* Company income */}
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <h3 className="flex items-center gap-2 text-base font-black text-slate-900">
                <Icon name="cash-stack" size="1rem" className="text-emerald-600" />
                Income
              </h3>
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  {selectedProvider ? selectedProvider.name : "All companies"}
                </p>
                <p className="text-sm font-black text-emerald-700">{formatMoney(totalIncome)}</p>
              </div>
            </div>
            <StatementTable
              rows={payments}
              rowKey={(payment, index) => getUtilitySettlementId(payment) || payment._id || index}
              emptyText="No income yet."
              columns={[
                {
                  label: "Date",
                  width: "20%",
                  render: (payment) => formatDateWithTime(payment.date),
                },
                {
                  label: "Customer",
                  align: "left",
                  render: (payment) => {
                    const description = getUtilityPaymentDescription(payment)
                    return (
                      <div>
                        <div className="font-semibold text-slate-800">{payment.customer?.name || "Customer"}</div>
                        <div className="text-slate-500">{payment.provider?.name || ""}</div>
                        {description && <div className="text-slate-400">{description}</div>}
                      </div>
                    )
                  },
                },
                {
                  label: "Src",
                  width: "16%",
                  align: "center",
                  render: (payment) => <IncomeSourceBadge source={payment.source} />,
                },
                {
                  label: "Amount",
                  width: "20%",
                  align: "right",
                  render: (payment) => {
                    const cashMovement = getProviderUtilityCashMovement(payment)
                    const isWaiver = payment?.kind === "waiver" || payment?.reversalOfKind === "waiver"
                    return (
                      <span className={`font-bold ${cashMovement < 0 ? "text-rose-700" : isWaiver ? "text-amber-700" : "text-emerald-700"}`}>
                        {cashMovement < 0 ? "-" : ""}{formatMoney(payment.amount)}
                      </span>
                    )
                  },
                },
              ]}
            />
          </section>
        </>
      )}

      {statementCustomer && selectedProvider && (
        <CustomerStatementModal
          providerId={selectedProvider._id}
          providerName={selectedProvider.name}
          customer={statementCustomer}
          onLoadStatement={onLoadCustomerStatement}
          onClose={() => setStatementCustomer(null)}
        />
      )}

      {modal === "category" && selectedProvider && (
        <AddCategoryModal
          providerId={selectedProvider._id}
          providerName={selectedProvider.name}
          onClose={() => setModal(null)}
          onCreate={onCreateCategory}
        />
      )}
      {modal === "bill" && selectedProvider && (
        <AddBillModal
          providerId={selectedProvider._id}
          providerName={selectedProvider.name}
          categories={categories}
          unitScopeEnabled={isManagementCompany}
          properties={properties}
          initialPropertyId={deepLinkPropertyId || selectedPropertyId}
          initialUnitId={deepLinkUnitId}
          onClose={() => setModal(null)}
          onCreateBill={onCreateBill}
          onCreate={onCreateInvoice}
          onSearchCustomers={onSearchCustomers}
        />
      )}
      {modal === "waive" && selectedProvider && selectedInvoice && (
        <WaiveBillModal
          providerId={selectedProvider._id}
          invoice={selectedInvoice}
          onClose={() => {
            setModal(null)
            setSelectedInvoice(null)
          }}
          onWaive={onWaiveInvoice}
        />
      )}
      {modal === "edit" && selectedProvider && selectedInvoice && (
        <EditInvoiceModal
          invoice={selectedInvoice}
          onClose={() => {
            setModal(null)
            setSelectedInvoice(null)
          }}
          onSave={(payload) => onEditInvoice(selectedProvider._id, selectedInvoice._id, payload)}
        />
      )}
      {modal === "autobill" && selectedProvider && (
        <AutoBillModal
          providerId={selectedProvider._id}
          providerName={selectedProvider.name}
          categories={companyCategories}
          onCreateCategory={onCreateCategory}
          onConfigure={onConfigureAutoInvoice}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}

export default BillingConsole
